<template>
    <sidebar @close="$emit('close')">
        <template v-slot:title v-if="reimbursement">
            {{ reimbursement.name }}
        </template>

        <template v-slot:header v-if="reimbursement">
            <div class="row mb-0">
                <div class="col">
                    <span class="text-muted">
                        {{ reimbursement.company.name }}
                    </span>
                </div>
            </div>
            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabReimbursementContentMain" id="tabReimbursementLinkMain" data-toggle="tab" role="tab" aria-controls="tabReimbursementContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content" v-if="reimbursement">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabReimbursementContentMain" role="tabpanel" aria-labelledby="tabReimbursementLinkMain">
                    <!-- Reimbursement type details widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Reimbursement Type</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editReimbursementType">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                            <button v-if="reimbursement.is_active" type="button" class="btn btn-outline-primary ml-1" @click.prevent="deactivate()">
                                Deactivate
                            </button>
                            <button v-else type="button" class="btn btn-outline-primary ml-1" @click.prevent="reactivate()">
                                Reactivate
                            </button>
                        </template>

                        <slot>
                            <div class="p-2">
                                <div>
                                    <span class="text-semibold">Name: </span>
                                    <span>{{ reimbursement.name }}</span>
                                </div>
                            </div>
                        </slot>
                    </info-widget>

                    <!-- Positions widget -->
                    <info-widget :collapsible="false">
                        <template v-slot:title>Positions ({{ reimbursement.num_positions }})</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editPositions">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div v-if="activePositionRels.length" class="p-1">
                                <table class="table css-table widget-table table-fixed">
                                    <thead>
                                        <tr>
                                            <th>Position</th>
                                            <th class="cw-6">Default</th>
                                        </tr>
                                    </thead>
                                    <tbody v-for="rel in activePositionRels" :key="rel.id">
                                        <tr>
                                            <td class="truncate-wrapper">
                                                <span>{{ rel.position.terminal_value }}</span>
                                            </td>
                                            <td class="cw-6">
                                                <i v-if="rel.is_default" class="text-muted fas fa-fw fa-check" aria-label="Yes"></i>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="info-widget-empty p-2" v-else>
                                This reimbursement is not associated with any positions.
                            </div>
                        </slot>
                    </info-widget>
                </div>
            </div>
        </slot>

        <edit-reimbursement-positions-modal v-if="reimbursement && editPositionsModalOpen" ref="editReimbursementPositionsModal" :reimbursement="reimbursement" :client="client" @updated="onUpdate" @close="editPositionsModalOpen = false" />
        <edit-reimbursement-type-modal v-if="reimbursement && editReimbursementTypeModalOpen" :reimbursement="reimbursement" :client="client" @updated="onUpdate" @close="editReimbursementTypeModalOpen = false" />
    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditReimbursementPositionsModal from './EditReimbursementPositionsModal'
import EditReimbursementTypeModal from './EditReimbursementTypeModal'

export default {
    props: ['reimbursementId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditReimbursementPositionsModal, EditReimbursementTypeModal},
    computed: {
        activePositionRels() {
            let ret = []
            if (this.reimbursement?.position_rels) {
                ret = this.reimbursement.position_rels.filter(r => r.position.is_active)
            }
            return ret
        }
    },
    data() {
        return {
            reimbursement: null,
            activeTab: this.tab || 'main',
            editPositionsModalOpen: false,
            editReimbursementTypeModalOpen: false,
        }
    },
    watch: {
        reimbursementId() {
            this.getReimbursement()
        }
    },
    mounted() {
        this.getReimbursement()
    },
    methods: {
        getReimbursement() {
            this.reimbursement = null
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/reimbursement-types/${this.reimbursementId}`).then(resp => {
                this.reimbursement = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        editReimbursementType() {
            this.editReimbursementTypeModalOpen = true
        },
        editPositions() {
            this.editPositionsModalOpen = true
        },
        deactivate() {
            if (!confirm('Are you sure you want to deactivate this reimbursement?')) {return}
            this.setStatus('inactive')
        },
        reactivate() {
            if (!confirm('Are you sure you want to reactivate this reimbursement?')) {return}
            this.setStatus('active')
        },
        setStatus(status) {
            this.$store.dispatch('START_LOADING')
            this.$api.patch(`/clients/${this.client.id}/settings/reimbursement-types/${this.reimbursement.id}`, {status}).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.onUpdate(resp)
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(reimbursement) {
            if (reimbursement) {
                Object.assign(this.reimbursement, reimbursement)
            }
            this.$emit('updated')
        }
    },
}
</script>
